import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from '@/lib/useInView'
import { useReducedMotion } from '@/lib/useReducedMotion'

const NODES = [
  { id: 'a', x: 60, y: 80, brand: false },
  { id: 'b', x: 180, y: 40, brand: true },
  { id: 'c', x: 300, y: 90, brand: false },
  { id: 'd', x: 100, y: 200, brand: false },
  { id: 'e', x: 220, y: 220, brand: true },
  { id: 'f', x: 340, y: 180, brand: false },
]
const EDGES: Array<[string, string]> = [
  ['a', 'b'], ['b', 'c'], ['a', 'd'], ['b', 'e'], ['c', 'f'], ['d', 'e'], ['e', 'f'],
]

// Per-node sway parameters: period (ms) and phase offset (radians)
const SWAY_PARAMS = NODES.map(() => ({
  period: 4000 + Math.random() * 2000,
  phase: Math.random() * Math.PI * 2,
}))

function edgeLength(from: string, to: string) {
  const a = NODES.find((n) => n.id === from)!
  const b = NODES.find((n) => n.id === to)!
  return Math.hypot(b.x - a.x, b.y - a.y)
}

export function HeroGraph() {
  const { t } = useTranslation()
  const { ref, inView } = useInView<SVGSVGElement>({ once: false })
  const reduced = useReducedMotion()
  const [nonce, setNonce] = useState(0)
  const [sway, setSway] = useState<number[]>(NODES.map(() => 0))
  const hoverFactorRef = useRef(1)
  const hoverTargetRef = useRef(1)
  const rafRef = useRef<number | null>(null)
  const swayStartRef = useRef<number | null>(null)
  const lastTsRef = useRef<number | null>(null)
  const phaseAccRef = useRef<number[]>(NODES.map(() => 0))

  // Re-trigger entrance animation whenever we come back into view
  useEffect(() => {
    if (!inView) return
    setNonce((n) => n + 1)
    swayStartRef.current = null
    lastTsRef.current = null
    phaseAccRef.current = NODES.map(() => 0)
  }, [inView])

  // Idle sway rAF loop — starts after phase 3 completes (~2300ms).
  // Uses a phase accumulator so hoverFactor changes only affect angular
  // velocity (going forward), never absolute phase position. This avoids
  // the giga-jiggle that the multiplicative t*f formulation produced after
  // long idle periods.
  useEffect(() => {
    if (reduced) return

    const SWAY_DELAY = 2300
    let startTs: number | null = null
    let launched = false

    function tick(ts: number) {
      if (!launched) {
        if (startTs === null) startTs = ts
        if (ts - startTs < SWAY_DELAY) {
          rafRef.current = requestAnimationFrame(tick)
          return
        }
        launched = true
        swayStartRef.current = ts
        lastTsRef.current = ts
      }

      // Ease hoverFactor toward target
      const factor = hoverFactorRef.current
      const target = hoverTargetRef.current
      if (Math.abs(factor - target) > 0.001) {
        hoverFactorRef.current += (target - factor) * 0.12
      } else {
        hoverFactorRef.current = target
      }

      const f = hoverFactorRef.current
      const last = lastTsRef.current ?? ts
      const dt = ts - last
      lastTsRef.current = ts

      const phases = phaseAccRef.current
      for (let i = 0; i < phases.length; i++) {
        phases[i] += (dt * f * Math.PI * 2) / SWAY_PARAMS[i].period
      }

      setSway(phases.map((p, i) => Math.sin(p + SWAY_PARAMS[i].phase) * 2))

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [reduced, nonce])

  const node = (id: string) => NODES.find((n) => n.id === id)!

  const onMouseEnter = () => { hoverTargetRef.current = 1.4 }
  const onMouseLeave = () => { hoverTargetRef.current = 1 }

  if (reduced) {
    // Static final state — no animation, no sway
    return (
      <svg
        ref={ref}
        viewBox="0 0 400 280"
        role="img"
        aria-label={t('alt.heroGraph')}
        className="w-full max-w-md mx-auto"
      >
        <g stroke="currentColor" strokeWidth="1.5">
          {EDGES.map(([from, to]) => {
            const a = node(from), b = node(to)
            return (
              <line
                key={`${from}-${to}`}
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                fill="none"
              />
            )
          })}
          {NODES.map((n) => (
            <circle
              key={n.id}
              cx={n.x} cy={n.y} r="6"
              fill={n.brand ? 'var(--brand-accent)' : 'currentColor'}
              stroke={n.brand ? 'var(--brand-accent)' : 'currentColor'}
              fillOpacity={1}
            />
          ))}
        </g>
      </svg>
    )
  }

  return (
    <svg
      ref={ref}
      viewBox="0 0 400 280"
      role="img"
      aria-label="Associative graph diagram"
      className="w-full max-w-md mx-auto"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <g key={nonce} stroke="currentColor" strokeWidth="1.5">
        {/* Phase 2: edges draw left-to-right via stroke-dashoffset, start at 1000ms */}
        {EDGES.map(([from, to], i) => {
          const a = node(from), b = node(to)
          const dy = (sway[NODES.findIndex((n) => n.id === from)] + sway[NODES.findIndex((n) => n.id === to)]) / 2
          const len = Math.ceil(edgeLength(from, to)) + 4
          const edgeDelay = 1000 + i * Math.round(1000 / EDGES.length)
          return (
            <line
              key={`${from}-${to}`}
              x1={a.x} y1={a.y + dy}
              x2={b.x} y2={b.y + dy}
              fill="none"
              strokeDasharray={len}
              strokeDashoffset={len}
              style={{
                animation: `ode-edge-draw 0.5s ${edgeDelay}ms forwards ease-out`,
              }}
            />
          )
        })}
        {/* Phase 1: circles slide from 16px y-offset + fade in; Phase 3: fill */}
        {NODES.map((n, i) => {
          const nodeDelay = i * 80
          const fillDelay = 2000 + i * Math.round(300 / NODES.length)
          return (
            <circle
              key={n.id}
              cx={n.x}
              cy={n.y + sway[i]}
              r="6"
              fill={n.brand ? 'var(--brand-accent)' : 'currentColor'}
              stroke={n.brand ? 'var(--brand-accent)' : 'currentColor'}
              fillOpacity={0}
              style={{
                animation: [
                  `ode-node-slide ${600}ms ${nodeDelay}ms forwards ease-out`,
                  `ode-node-fill 300ms ${fillDelay}ms forwards ease-out`,
                ].join(', '),
              }}
            />
          )
        })}
      </g>
      <style>{`
        @keyframes ode-node-slide {
          0%   { opacity: 0; transform: translateY(16px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes ode-edge-draw {
          to { stroke-dashoffset: 0; }
        }
        @keyframes ode-node-fill {
          to { fill-opacity: 1; }
        }
      `}</style>
    </svg>
  )
}
